import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { containerTypeColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import type { ApiContainer, ApiContainerType } from '@Model';
import { EnumFrontendLayoutSetting } from '@Model';
import { FrameEditContainerType } from '@Modules/Stammdaten';
import {
    Card,
    DateInput,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    NumberInput,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedFieldInFrame,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useParams } from 'react-router';

export function FrameEditContainer(props: IFrameEditShellProps<ApiContainer>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiContainer>
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            apiResourcePath={`${STAMMDATEN_API}/containers`}
            renderer={FrameEditContainerBody}
            canEdit={props.canEdit !== false && hasPermission(Permission.StammdatenContainerUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.StammdatenContainerCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.StammdatenContainerDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditContainerBody(props: IFrameBodyProps<ApiContainer>) {
    const i18n = useTranslate('');

    const binder = useGuiBinder<ApiContainer>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });

    const linkType = useLinkedFieldInFrame(props, 'type', FrameEditContainerType);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Container', props.mode),
                resourceLabel: 'Container',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="PrimaryContainerData" border={'withoutBorder'}>
                <Grid>
                    <GridColumns spans={[6, 6, 12]}>
                        <TextInput {...binder.bindToProp<string>('number')} label={i18n('Nummer')} />
                        <LinkedFieldWithLayoutSettings<ApiContainerType>
                            layoutSettingsContext={EnumFrontendLayoutSetting.ContainerTypesTableLayout}
                            {...binder.bindToLinkProperty(linkType.linkApi)}
                            {...linkType.events}
                            columns={containerTypeColumns}
                            label={i18n('Typ')}
                        />
                        <TextInput {...binder.bindToProp<string>('remarks')} inputSize={3} label={i18n('Bemerkung')} />
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="ContainerType" label="Technik">
                <Grid>
                    <GridColumns spans={[6, 6, 6]}>
                        <DateInput
                            type="day"
                            {...binder.bindToProp<string>('next_inspection_date_internal')}
                            label={i18n('Prüfung Intern')}
                        />
                        <DateInput
                            type="day"
                            {...binder.bindToProp<string>('next_inspection_date')}
                            label={i18n('Prüfung Extern')}
                        />
                        <NumberInput {...binder.bindToProp<number>('empty_weight')} label={i18n('Leergewicht (t)')} />
                    </GridColumns>
                </Grid>
            </Card>
        </EditFrameShell>
    );
}
